import * as React from 'react'
import * as SheetPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * A side drawer built on Radix Dialog — a full-height panel over a scrim, for
 * views that do not belong in a centered dialog: something too big for one (the
 * furniture gallery, anchored right) or navigation, which belongs at the edge it
 * slides from (the mobile nav tray, anchored left).
 *
 * Same a11y as Dialog — focus trap, Escape, scrim click — and it still needs a
 * `SheetTitle`, visually hidden if the panel's purpose is already obvious.
 */
export const Sheet = SheetPrimitive.Root
export const SheetTrigger = SheetPrimitive.Trigger

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {
  /** Which edge it slides from. Right by default — the original behavior. */
  side?: 'left' | 'right'
}

/** Edge-dependent placement: the border and the slide are on the anchored side. */
const SIDE: Record<'left' | 'right', string> = {
  right: 'right-0 border-l motion-safe:animate-[ds-sheet-in_200ms_var(--ds-ease-out)]',
  left: 'left-0 border-r motion-safe:animate-[ds-sheet-in-left_200ms_var(--ds-ease-out)]',
}

export const SheetContent = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(function SheetContent({ className, children, side = 'right', ...props }, ref) {
  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay className="fixed inset-0 z-40 bg-scrim" />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          'fixed inset-y-0 z-50 flex h-full w-full max-w-[640px] flex-col border-line bg-surface text-ink shadow-lg outline-none',
          SIDE[side],
          className,
        )}
        {...props}
      >
        {children}
        <SheetPrimitive.Close
          aria-label="Close"
          className="absolute right-4 top-4 grid h-[30px] w-[30px] place-items-center rounded-md text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink focus-visible:focus-ds"
        >
          <X className="h-4 w-4" />
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  )
})

export function SheetHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('shrink-0 border-b border-line py-4 pl-6 pr-14', className)} {...props} />
}

export const SheetTitle = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(function SheetTitle({ className, ...props }, ref) {
  return <SheetPrimitive.Title ref={ref} className={cn('truncate text-title-sm font-semibold text-ink', className)} {...props} />
})

export const SheetDescription = React.forwardRef<
  React.ComponentRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(function SheetDescription({ className, ...props }, ref) {
  return <SheetPrimitive.Description ref={ref} className={cn('mt-0.5 text-sm text-ink-muted', className)} {...props} />
})
